/**
 * HowToPage.jsx — Knowledge library: How-To videos + Inspiration references.
 *
 * - Two tabs (kind: howto | inspiration), tab/search/category kept in the URL
 *   so back-navigation from a detail page restores the view
 * - Quick-add bar: paste a video URL + Enter → entry created via oEmbed autofill
 * - Search spans BOTH tabs (results show a kind badge); category chips are
 *   computed per-tab
 * - One inline player at a time (playingId)
 * - Detail subpage: /howto/:id
 */
import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, InputAdornment, Stack, Chip,
  Snackbar, Alert, Tabs, Tab, Paper, CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  School as SchoolIcon,
  AutoAwesome as InspirationIcon,
  PlayCircleOutline as PlayCircleIcon,
} from '@mui/icons-material';
import {
  useWikiArticles, useCreateWikiArticle, useUpdateWikiArticle, useDeleteWikiArticle,
} from '../hooks/useWiki';
import { useRBAC } from '../hooks/useRBAC';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../hooks/useAuth';
import { useMobile } from '../hooks/useMobile';
import { useFocus } from '../hooks/useFocus';
import { api } from '../lib/api';
import { parseVideoUrl } from '../lib/videoUrl';
import HowToCard from '../components/howto/HowToCard';
import HowToEditDialog from '../components/howto/HowToEditDialog';

const KINDS = ['howto', 'inspiration'];

export default function HowToPage() {
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const { hasAccess } = useRBAC();
  const { data: settings } = useSettings();
  const { profile } = useAuth();
  const { isHidden } = useFocus();
  const canEdit = hasAccess('wiki', 'edit', settings);
  const canDelete = hasAccess('wiki', 'full', settings);

  const { data: entries = [], isLoading } = useWikiArticles();
  const createMut = useCreateWikiArticle();
  const updateMut = useUpdateWikiArticle();
  const deleteMut = useDeleteWikiArticle();

  // ── URL-backed view state ──
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = KINDS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'howto';
  const search = searchParams.get('q') || '';
  const activeCategory = searchParams.get('cat') || 'All';
  // Focus hides the Inspiration tab only while it's empty and not the open tab
  const hideInspirationTab = isHidden('howto.inspirationTab') && tab !== 'inspiration'
    && !entries.some((e) => e.kind === 'inspiration');

  const setParam = (key, value, defaultValue) => {
    // Changing the search/category filter re-renders the grid; a card that
    // unmounts/remounts would re-autoplay, so stop the inline player first.
    setPlayingId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!value || value === defaultValue) next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: key === 'q' });
  };

  const [playingId, setPlayingId] = useState(null);
  const [editTarget, setEditTarget] = useState(null); // null=closed; {}=new; {id..}=edit
  const [quickUrl, setQuickUrl] = useState('');
  const [quickAdding, setQuickAdding] = useState(false);
  const [snack, setSnack] = useState(null);

  // ?action=newHowto (Command Palette) → open the dialog once (editors only)
  useEffect(() => {
    if (searchParams.get('action') !== 'newHowto') return;
    if (canEdit) {
      setEditTarget({ kind: searchParams.get('tab') === 'inspiration' ? 'inspiration' : 'howto' });
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('action');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams, canEdit]);

  const searching = search.trim().length > 0;

  // Search spans both tabs; otherwise filter by kind + category
  const visible = useMemo(() => {
    if (searching) {
      const q = search.trim().toLowerCase();
      return entries.filter((e) =>
        (e.title || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q) ||
        (e.bodyMd || '').toLowerCase().includes(q) ||
        (e.category || '').toLowerCase().includes(q),
      );
    }
    let list = entries.filter((e) => (e.kind || 'howto') === tab);
    if (activeCategory !== 'All') list = list.filter((e) => (e.category || 'General') === activeCategory);
    return list;
  }, [entries, searching, search, tab, activeCategory]);

  const tabEntries = useMemo(
    () => entries.filter((e) => (e.kind || 'howto') === tab),
    [entries, tab],
  );

  const categories = useMemo(() => {
    const counts = new Map();
    for (const e of tabEntries) {
      const c = e.category || 'General';
      counts.set(c, (counts.get(c) || 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]));
  }, [tabEntries]);

  const existingCategoryNames = useMemo(
    () => Array.from(new Set(entries.map((e) => e.category).filter(Boolean))),
    [entries],
  );

  // ── Quick add: paste URL + Enter ──
  const handleQuickAdd = async () => {
    const raw = quickUrl.trim();
    if (!raw) return;
    const withProto = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const video = parseVideoUrl(withProto);
    if (!video) {
      // Not a video link → full dialog, prefilled
      setEditTarget({ url: withProto, kind: tab });
      setQuickUrl('');
      return;
    }
    setQuickAdding(true);
    try {
      let meta = null;
      try {
        meta = await api.get(`/oembed?url=${encodeURIComponent(withProto)}`);
      } catch { /* oEmbed failed → fall through to dialog for a manual title */ }
      if (!meta?.title) {
        setEditTarget({ url: withProto, kind: tab });
        setQuickUrl('');
        return;
      }
      const created = await createMut.mutateAsync({
        title: meta.title,
        kind: tab,
        category: 'General',
        url: withProto,
        thumbnailUrl: meta.thumbnailUrl || null,
        createdBy: profile?.id || null,
      });
      setQuickUrl('');
      // Clear any active search/category filter so the just-added card is visible.
      if (searching || activeCategory !== 'All') {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('q');
          next.delete('cat');
          return next;
        }, { replace: true });
      }
      setSnack({ kind: 'success', msg: `Added "${meta.title}"`, editEntry: created });
    } catch (err) {
      setSnack({ kind: 'error', msg: err.message || 'Could not add entry' });
    } finally {
      setQuickAdding(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      if (payload.id) {
        await updateMut.mutateAsync({ ...payload, updatedBy: profile?.id || null });
        setSnack({ kind: 'success', msg: 'Entry updated' });
      } else {
        await createMut.mutateAsync({ ...payload, createdBy: profile?.id || null });
        setSnack({ kind: 'success', msg: 'Entry added' });
      }
      setEditTarget(null);
    } catch (err) {
      setSnack({ kind: 'error', msg: err.message || 'Save failed' });
    }
  };

  const handleDelete = async () => {
    if (!editTarget?.id) return;
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    try {
      await deleteMut.mutateAsync(editTarget.id);
      setSnack({ kind: 'success', msg: 'Entry deleted' });
      setEditTarget(null);
    } catch (err) {
      setSnack({ kind: 'error', msg: err.message || 'Delete failed' });
    }
  };

  const saving = createMut.isPending || updateMut.isPending || deleteMut.isPending;
  const TabIcon = tab === 'howto' ? SchoolIcon : InspirationIcon;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon sx={{ color: '#10B981' }} />
          <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1 }}>
            How To
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
          </Typography>
        </Box>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setEditTarget({ kind: tab })}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            {isMobile ? 'Add' : 'Add Entry'}
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => {
          // Single setSearchParams call — two consecutive functional updates
          // race each other (the second navigates with stale params)
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (v === 'howto') next.delete('tab'); else next.set('tab', v);
            next.delete('cat');
            return next;
          });
          setPlayingId(null);
        }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="howto" label="How-To" icon={<SchoolIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: 'none', minHeight: 44 }} />
        {!hideInspirationTab && <Tab data-focus="howto.inspirationTab" value="inspiration" label="Inspiration" icon={<InspirationIcon fontSize="small" />} iconPosition="start" sx={{ textTransform: 'none', minHeight: 44 }} />}
      </Tabs>

      {/* Quick add */}
      {canEdit && (
        <TextField
          fullWidth
          size="small"
          placeholder={`Paste a YouTube, Vimeo, or Instagram link and press Enter to add it to ${tab === 'howto' ? 'How-To' : 'Inspiration'}…`}
          value={quickUrl}
          onChange={(e) => setQuickUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !quickAdding) handleQuickAdd(); }}
          disabled={quickAdding}
          InputProps={{
            startAdornment: <InputAdornment position="start"><PlayCircleIcon fontSize="small" sx={{ color: '#10B981' }} /></InputAdornment>,
            endAdornment: quickAdding ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null,
          }}
          sx={{ mb: 1.5 }}
        />
      )}

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search all entries…"
        value={search}
        onChange={(e) => setParam('q', e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
        sx={{ mb: 2 }}
      />

      {/* Category chips (per-tab; hidden while searching) */}
      {!searching && categories.length > 1 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={`All (${tabEntries.length})`}
            color={activeCategory === 'All' ? 'primary' : 'default'}
            variant={activeCategory === 'All' ? 'filled' : 'outlined'}
            onClick={() => setParam('cat', null)}
            size="small"
          />
          {categories.map(([cat, count]) => (
            <Chip
              key={cat}
              label={`${cat} (${count})`}
              color={activeCategory === cat ? 'primary' : 'default'}
              variant={activeCategory === cat ? 'filled' : 'outlined'}
              onClick={() => setParam('cat', cat === activeCategory ? null : cat)}
              size="small"
            />
          ))}
        </Stack>
      )}

      {searching && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {visible.length} {visible.length === 1 ? 'match' : 'matches'} across How-To and Inspiration
        </Typography>
      )}

      {/* Grid */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      ) : visible.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
          <TabIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
          {searching ? (
            <Typography variant="body2" color="text.secondary">No matches for “{search}”.</Typography>
          ) : activeCategory !== 'All' ? (
            // Stale/empty category filter — there ARE entries, just not here
            <>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Nothing in “{activeCategory}”
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {tabEntries.length > 0
                  ? `This ${tab === 'howto' ? 'How-To' : 'Inspiration'} section has other categories.`
                  : 'This section is empty.'}
              </Typography>
              <Button variant="outlined" size="small" onClick={() => setParam('cat', null)} sx={{ textTransform: 'none' }}>
                Show all
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {tab === 'howto' ? 'No how-tos yet' : 'No inspiration saved yet'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {canEdit
                  ? 'Paste a YouTube, Vimeo, or Instagram link in the bar above — the title fills itself in.'
                  : 'Nothing here yet — check back soon.'}
              </Typography>
              {canEdit && (
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setEditTarget({ kind: tab })} sx={{ textTransform: 'none' }}>
                  Add the first entry
                </Button>
              )}
            </>
          )}
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 2 }}>
          {visible.map((entry) => (
            <HowToCard
              key={entry.id}
              entry={entry}
              playing={playingId === entry.id}
              onPlay={setPlayingId}
              onOpen={(id) => navigate(`/howto/${id}`, { state: { canGoBack: true } })}
              onEdit={setEditTarget}
              canEdit={canEdit}
              isMobile={isMobile}
              showKind={searching}
            />
          ))}
        </Box>
      )}

      {/* Add/Edit dialog */}
      <HowToEditDialog
        open={!!editTarget}
        initial={editTarget}
        existingCategories={existingCategoryNames}
        onClose={() => setEditTarget(null)}
        onSave={handleSave}
        onDelete={handleDelete}
        canDelete={canDelete}
        saving={saving}
      />

      {/* Toast */}
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert
            severity={snack.kind}
            onClose={() => setSnack(null)}
            variant="filled"
            action={snack.editEntry ? (
              <Button color="inherit" size="small" onClick={() => { setEditTarget(snack.editEntry); setSnack(null); }}>
                Edit
              </Button>
            ) : undefined}
          >
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
